import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import AnalyzerPage from '../components/AnalyzerPage';
import { useVersion } from '../contexts/VersionContext';
import jobAnalyzerService from '../services/jobAnalyzerService';
import { saveViewedUser, VIEW_CONTEXTS } from '../utils/viewedUserStore';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/localization/localization';
import { logger, fileLogger } from '../utils/logger';
import dataTypeService from '../services/dataTypeService';
import { usePerformanceMonitor } from '../utils/performanceMonitor';
import { LoadingState, ErrorState, tableShellSx, responsiveTableContainerSx } from '../components/common';

const JOB_IMPORTANCE_LEVELS = ['Requis', 'Prefere', 'Bon a avoir'];

/**
 * Job Analyzer Page - Refactored to use shared AnalyzerPage component
 */
function JobAnalyzerPage({ userId, viewOnly = false, onComplete, onStatusChange }) {
  // Add state to track if company research exists
  const [companyResearchExists, setCompanyResearchExists] = useState(false);
  const [checkingCompanyResearch, setCheckingCompanyResearch] = useState(true);
  const [companyResearchError, setCompanyResearchError] = useState('');

  // State for categories
  const [categories, setCategories] = useState([]);
  const [_categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoryLoadError, setCategoryLoadError] = useState('');

  // State for regenerate matching loading
  const [regeneratingMatching, setRegeneratingMatching] = useState(false);
  const versionContext = useVersion();
  const { currentUser } = useAuth();
  useLanguage(); // Just for context subscription

  // Load categories from database
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setCategoriesLoading(true);
        setCategoryLoadError('');
        const dbCategories = await jobAnalyzerService.getCategories();
        setCategories(dbCategories);
        logger.info('JOB_ANALYZER', 'Categories loaded from database', {
          categoriesCount: dbCategories.length,
          categories: dbCategories
        });
      } catch (error) {
        logger.error('JOB_ANALYZER', 'Error loading categories', error);
        setCategories([]);
        setCategoryLoadError(error.message || t('errors.genericError'));
      } finally {
        setCategoriesLoading(false);
      }
    };
    loadCategories();
  }, []);

  // Add performance monitoring
  const performanceMetrics = usePerformanceMonitor(
    'JobAnalyzerPage',
    {
      userId,
      viewOnly,
      currentUserId: currentUser?.id
    },
    {
      companyResearchExists,
      checkingCompanyResearch
    }
  );

  // Helper function to render matching percentage with colored circular progress
  const renderMatchingProgress = (skill) => {
    const matching = skill.matching;
    if (!matching || matching.percentage === undefined) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            N/A
          </Typography>
        </Box>
      );
    }

    const percentage = matching.percentage;
    const getColor = (percent) => {
      if (percent >= 80) return '#4caf50'; // Green
      if (percent >= 60) return '#ff9800'; // Orange
      if (percent >= 40) return '#ff5722'; // Red-orange
      return '#f44336'; // Red
    };

    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          cursor: 'pointer'
        }}
        title={matching.justification || 'No justification provided'}
      >
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={40}
          thickness={4}
          sx={{
            color: getColor(percentage),
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round'
            }
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}
          >
            {`${percentage}%`}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Log performance warnings
  useEffect(() => {
    if (performanceMetrics.isExcessiveRenders) {
      logger.warn(
        'PERFORMANCE',
        'JobAnalyzerPage experiencing excessive re-renders',
        {
          renderCount: performanceMetrics.renderCount,
          windowCount: performanceMetrics.renderWindowCount,
          userId,
          viewOnly,
          companyResearchExists
        }
      );
    }
  }, [performanceMetrics, userId, viewOnly, companyResearchExists]);

  // Function to ensure cross-user version synchronization for team management
  const synchronizeUserVersions = useCallback(async (targetUserId) => {
    try {
      logger.info('JOB_ANALYZER', 'Starting version synchronization for user in team management context', {
        targetUserId,
        viewOnly,
        location: window.location.href,
        timestamp: new Date().toISOString()
      });

      // Database storage doesn't require manually capturing key states like localStorage did
      // Instead, we'll directly check if data exists for this user
      const dataExists = await dataTypeService.loadJobSkills(targetUserId);

      logger.info('JOB_ANALYZER', 'Job data synchronization check complete', {
        targetUserId,
        dataExists: Boolean(dataExists && dataExists.result),
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      logger.error('JobAnalyzerPage', 'Error during job data synchronization', error);
      logger.error('JOB_ANALYZER', 'Error synchronizing job data versions', {
        error: error.message,
        targetUserId
      });
    }
  }, [viewOnly]);

  // CROSS-USER VERSION FIX: Ensure versions persist across browser refreshes in team management
  useEffect(() => {
    if (userId) {
      // Record that we're viewing this user's data (critical for persistence across page refreshes)
      if (userId !== versionContext.currentUser?.id) {
        fileLogger.info('JOB_ANALYZER', 'Recording viewed subordinate for persistence', {
          userId,
          currentUserId: versionContext.currentUser?.id,
          viewContext: VIEW_CONTEXTS.TEAM_MANAGEMENT
        });
        saveViewedUser(userId, VIEW_CONTEXTS.TEAM_MANAGEMENT);
      }

      // Synchronize versions for this user
      synchronizeUserVersions(userId);
    }
  }, [userId, versionContext.currentUser?.id, synchronizeUserVersions]);

  // Get initial state for the Job analyzer
  const getInitialState = () => ({
    skills: [],
    combinedText: '',
    files: [],
    editingSkill: null,
    isNewSkill: false,
    selectedSkillIndex: null,
    // Filter states
    categoryFilter: '',
    importanceFilter: '',
    matchingFilter: '',
    categoryFilterAnchorEl: null,
    importanceFilterAnchorEl: null,
    matchingFilterAnchorEl: null
  });

  // Process uploaded files (simple pass-through to analyzer)
  const processFiles = async (files, text) => {
    return { files, text };
  };

  // Get importance level color
  const getImportanceColor = (importance) => {
    switch(importance) {
      case 'Requis': return 'error';  // Red for required
      case 'Prefere': return 'warning';  // Orange for preferred
      case 'Bon a avoir': return 'info';  // Blue for nice to have
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getImportanceLabel = (importance) => {
    switch (importance) {
      case 'Requis':
        return t('jobAnalyzer.required');
      case 'Prefere':
        return t('jobAnalyzer.preferred');
      case 'Bon a avoir':
        return t('jobAnalyzer.niceToHave');
      default:
        return importance;
    }
  };

  // Render edit dialog wrapper function - matches AnalyzerPage expected signature
  const renderEditDialog = (editingItem, editingIndex, dialogProps) => {
    const { open, onClose, onSave } = dialogProps;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? t('jobAnalyzer.editSkill') : t('jobAnalyzer.addNewSkill')}
        </DialogTitle>
        <DialogContent>
          <EditSkillForm
            skill={editingItem}
            categories={categories}
            onSave={onSave}
            onClose={onClose}
          />
        </DialogContent>
      </Dialog>
    );
  };

  // Edit skill form component
  const EditSkillForm = ({ skill, categories, onSave, onClose }) => {
    const [editingSkill, setEditingSkill] = useState(() => {
      logger.info('JOB_ANALYZER', 'EditSkillForm initializing', { skill, categoriesLength: categories.length });
      return {
        name: skill?.name || skill?.skill || '',  // Handle both 'name' and 'skill' properties
        category: skill?.category || categories[0] || 'Competences techniques',
        importance: skill?.importance || skill?.level || JOB_IMPORTANCE_LEVELS[0],  // Handle both 'importance' and 'level'
        context: skill?.context || ''
      };
    });

    // Update form when skill prop changes (for editing existing skills)
    useEffect(() => {
      logger.info('JOB_ANALYZER', 'EditSkillForm useEffect triggered', { skill, categoriesLength: categories.length });
      if (skill) {
        const newState = {
          name: skill.name || skill.skill || '',  // Handle both 'name' and 'skill' properties
          category: skill.category || categories[0] || 'Competences techniques',
          importance: skill.importance || skill.level || JOB_IMPORTANCE_LEVELS[0],  // Handle both 'importance' and 'level'
          context: skill.context || ''
        };
        logger.info('JOB_ANALYZER', 'Setting editing skill state', newState);
        setEditingSkill(newState);
      }
    }, [skill, categories]);

    const handleSave = () => {
      onSave(editingSkill);
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <TextField
          label={t('jobAnalyzer.skillName')}
          value={editingSkill.name}
          onChange={(e) => setEditingSkill({
            ...editingSkill,
            name: e.target.value
          })}
          fullWidth
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>{t('jobAnalyzer.category')}</InputLabel>
            <Select
              value={editingSkill.category}
              onChange={(e) => setEditingSkill({
                ...editingSkill,
                category: e.target.value
              })}
              label={t('jobAnalyzer.category')}
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t('jobAnalyzer.importance')}</InputLabel>
            <Select
              value={editingSkill.importance}
              onChange={(e) => setEditingSkill({
                ...editingSkill,
                importance: e.target.value
              })}
              label={t('jobAnalyzer.importance')}
            >
              <MenuItem value={JOB_IMPORTANCE_LEVELS[0]}>{t('jobAnalyzer.required')}</MenuItem>
              <MenuItem value={JOB_IMPORTANCE_LEVELS[1]}>{t('jobAnalyzer.preferred')}</MenuItem>
              <MenuItem value={JOB_IMPORTANCE_LEVELS[2]}>{t('jobAnalyzer.niceToHave')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TextField
          label={t('jobAnalyzer.contextEvidence')}
          value={editingSkill.context}
          onChange={(e) => setEditingSkill({
            ...editingSkill,
            context: e.target.value
          })}
          multiline
          rows={4}
          fullWidth
        />

        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Box>
    );
  };

  // Handle removing a skill
  const handleRemoveSkill = (index, analysisResult, setAnalysisResult) => {
    // Check if analysisResult and skills exist
    if (!analysisResult || !analysisResult.skills) {
      logger.error('JOB_ANALYZER', 'Cannot remove skill: analysisResult or skills is null/undefined', {
        analysisResult: !!analysisResult,
        hasSkills: !!(analysisResult && analysisResult.skills),
        index
      });
      return;
    }

    // Create a new array without the removed skill
    const updatedSkills = [...analysisResult.skills];
    const removedSkill = updatedSkills[index];
    updatedSkills.splice(index, 1);

    // Update the analysis result
    const updatedResult = { ...analysisResult, skills: updatedSkills };
    setAnalysisResult(updatedResult);

    logger.info('JOB_ANALYZER', 'Skill removed successfully', {
      removedSkill: removedSkill,
      remainingSkillsCount: updatedSkills.length,
      index
    });

    // Log to file for monitoring
    fileLogger.info('JOB_ANALYZER', 'Skill deletion completed', {
      skillName: removedSkill?.name || removedSkill?.skill,
      skillCategory: removedSkill?.category,
      originalCount: analysisResult.skills.length,
      newCount: updatedSkills.length,
      deletedIndex: index
    });
  };

  // Handle regenerating CV-job matching percentages
  const handleRegenerateMatching = async (analysisResult, setAnalysisResult) => {
    if (!analysisResult || !analysisResult.skills) {
      logger.error('JOB_ANALYZER', 'Cannot regenerate matching: no analysis result or skills found');
      return;
    }

    const targetUserId = userId || currentUser?.id;
    if (!targetUserId) {
      logger.error('JOB_ANALYZER', 'Cannot regenerate matching: no user ID available');
      return;
    }

    try {
      // Set loading state
      setRegeneratingMatching(true);

      logger.info('JOB_ANALYZER', 'Starting CV-job matching regeneration', {
        skillsCount: analysisResult.skills.length,
        userId: targetUserId
      });

      // Call backend to regenerate only the matching data
      const response = await jobAnalyzerService.regenerateMatching(targetUserId);

      if (response && response.result && response.result.skills) {
        // Update the analysis result with the new matching data
        const updatedResult = { ...analysisResult, skills: response.result.skills };

        // Force UI update by creating a completely new object with updated timestamp
        const forceUpdateResult = {
          ...updatedResult,
          lastUpdated: new Date().toISOString(),
          regenerateTimestamp: Date.now() // Force reactivity
        };

        setAnalysisResult(forceUpdateResult);

        // Log the specific changes for debugging
        logger.info('JOB_ANALYZER', 'CV-job matching regenerated - UI update triggered', {
          skillsCount: response.result.skills.length,
          userId: targetUserId,
          updatedSkills: response.result.skills.map(skill => ({
            name: skill.name || skill.skill,
            oldMatching: analysisResult.skills.find(s => (s.name || s.skill) === (skill.name || skill.skill))?.matching,
            newMatching: skill.matching
          }))
        });

        // Log to file for monitoring
        fileLogger.info('JOB_ANALYZER', 'CV-job matching regeneration completed with UI refresh', {
          skillsCount: response.result.skills.length,
          userId: targetUserId,
          timestamp: new Date().toISOString(),
          forceUpdate: true
        });
      } else {
        throw new Error('Invalid response from regenerate matching API');
      }

    } catch (error) {
      logger.error('JOB_ANALYZER', 'Error regenerating CV-job matching', {
        error: error.message,
        userId: targetUserId
      });

      // Log to file for monitoring
      fileLogger.error('JOB_ANALYZER', 'CV-job matching regeneration failed', {
        error: error.message,
        userId: targetUserId,
        timestamp: new Date().toISOString()
      });
    } finally {
      // Clear loading state
      setRegeneratingMatching(false);
    }
  };

  // Helper function to get unique values for filters
  const getUniqueValues = (data, key) => {
    if (!data || !Array.isArray(data)) return [];
    return [...new Set(data.map(item => item[key]).filter(Boolean))].sort();
  };

  // Render the job skills display
  const renderDataDisplay = (
    analysisResult,
    customState,
    updateCustomState,
    setOpenEditDialog,
    setAnalysisResult,
    handleRemoveItem,
    _handleSaveItem,
    startEditing,
    _stopEditing
  ) => {
    if (!analysisResult) return null;

    const allSkills = analysisResult.skills || [];

    // Get unique categories and importance levels for filters
    const categories = getUniqueValues(allSkills, 'category');
    const importanceLevels = getUniqueValues(allSkills, 'importance').concat(getUniqueValues(allSkills, 'level')).filter((v, i, a) => a.indexOf(v) === i);

    // Helper function to get matching range from percentage
    const getMatchingRange = (skill) => {
      const matching = skill.matching;
      if (!matching || matching.percentage === undefined) return 'N/A';
      const percentage = matching.percentage;
      if (percentage >= 80) return 'High';
      if (percentage >= 60) return 'Medium';
      return 'Low';
    };

    // Apply filters
    const skills = allSkills.filter(skill => {
      const categoryMatch = !customState.categoryFilter || skill.category === customState.categoryFilter;
      const skillImportance = skill.importance || skill.level;
      const importanceMatch = !customState.importanceFilter || skillImportance === customState.importanceFilter;
      const matchingRange = getMatchingRange(skill);
      const matchingMatch = !customState.matchingFilter || matchingRange === customState.matchingFilter;
      return categoryMatch && importanceMatch && matchingMatch;
    });

    // Log filtering information
    fileLogger.info('JOB_ANALYZER', `Rendering job skills display with ${skills.length} skills (filtered from ${allSkills.length})`, {
      totalSkills: allSkills.length,
      filteredSkills: skills.length,
      categoryFilter: customState.categoryFilter,
      importanceFilter: customState.importanceFilter,
      matchingFilter: customState.matchingFilter
    });

    // Handle filter menu close for each column
    const handleCategoryFilterClose = () => {
      updateCustomState(prevState => ({ ...prevState, categoryFilterAnchorEl: null }));
    };

    const handleImportanceFilterClose = () => {
      updateCustomState(prevState => ({ ...prevState, importanceFilterAnchorEl: null }));
    };

    const handleMatchingFilterClose = () => {
      updateCustomState(prevState => ({ ...prevState, matchingFilterAnchorEl: null }));
    };

    // Handle filter selection for each column
    const handleCategoryFilterSelect = (value) => {
      updateCustomState(prevState => ({
        ...prevState,
        categoryFilter: value,
        categoryFilterAnchorEl: null
      }));
    };

    const handleImportanceFilterSelect = (value) => {
      updateCustomState(prevState => ({
        ...prevState,
        importanceFilter: value,
        importanceFilterAnchorEl: null
      }));
    };

    const handleMatchingFilterSelect = (value) => {
      updateCustomState(prevState => ({
        ...prevState,
        matchingFilter: value,
        matchingFilterAnchorEl: null
      }));
    };

    const jobTableColumnSx = {
      skill: { width: { xs: '28%', md: '26%' }, minWidth: { xs: 180, md: 220 } },
      category: { width: { xs: '20%', md: '19%' }, minWidth: { xs: 140, md: 170 } },
      importance: { width: { xs: '18%', md: '15%' }, minWidth: { xs: 120, md: 140 }, whiteSpace: 'nowrap' },
      matching: { width: { xs: '34%', md: '32%' }, minWidth: { xs: 220, md: 280 } },
      actions: { width: { xs: '1%', md: '8%' }, minWidth: 96, whiteSpace: 'nowrap' }
    };

    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 2 }}>
            {t('jobAnalyzer.skillsAnalysisResults')}
          </Typography>

          {/* Filter status indicator - only show if filters are active */}
          {(customState.categoryFilter || customState.importanceFilter || customState.matchingFilter) && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Chip
                icon={<FilterAltIcon />}
                label={`${t('common.filtered')}: ${customState.categoryFilter ? `${t('jobAnalyzer.category')}=${customState.categoryFilter}` : ''} ${customState.importanceFilter ? `${t('jobAnalyzer.importance')}=${getImportanceLabel(customState.importanceFilter)}` : ''} ${customState.matchingFilter ? `${t('jobAnalyzer.matching')}=${customState.matchingFilter}` : ''}`}
                onDelete={() => updateCustomState(prevState => ({ ...prevState, categoryFilter: '', importanceFilter: '', matchingFilter: '' }))}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          )}

          {categoryLoadError && (
            <Box sx={{ mb: 2 }}>
              <ErrorState
                error={categoryLoadError}
                title={t('common.error')}
                message={t('jobAnalyzer.categoryLoadError')}
                severity="warning"
                component="JobAnalyzer-Categories"
                showRetry={false}
              />
            </Box>
          )}

          {!viewOnly && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={regeneratingMatching ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={() => handleRegenerateMatching(analysisResult, setAnalysisResult)}
                disabled={
                  !analysisResult ||
                  !analysisResult.skills ||
                  analysisResult.skills.length === 0 ||
                  regeneratingMatching
                }
              >
                {regeneratingMatching ? t('jobAnalyzer.regeneratingMatching') : t('jobAnalyzer.regenerateMatching')}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  if (startEditing) {
                    // Pass null for new item, -1 for index to indicate new item
                    startEditing(null, -1);
                  }
                }}
              >
                {t('jobAnalyzer.addSkill')}
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={tableShellSx}>
          <TableContainer component={Paper} sx={responsiveTableContainerSx}>
            <Table sx={{ tableLayout: 'fixed', minWidth: { xs: 780, md: 960 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={jobTableColumnSx.skill}>{t('jobAnalyzer.skill')}</TableCell>
                  <TableCell sx={jobTableColumnSx.category}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={(e) => {
                      // Handle category filter menu click
                        const anchorEl = e.currentTarget;
                        // Set anchor for filter menu
                        updateCustomState(prevState => ({
                          ...prevState,
                          categoryFilterAnchorEl: anchorEl
                        }));
                      }}>
                      {t('jobAnalyzer.category')}
                      <FilterListIcon fontSize="small" sx={{ ml: 0.5, color: customState.categoryFilter ? 'primary.main' : 'action.disabled' }} />
                    </Box>
                  </TableCell>
                  <TableCell sx={jobTableColumnSx.importance}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={(e) => {
                      // Handle importance filter menu click
                        const anchorEl = e.currentTarget;
                        // Set anchor for filter menu
                        updateCustomState(prevState => ({
                          ...prevState,
                          importanceFilterAnchorEl: anchorEl
                        }));
                      }}>
                      {t('jobAnalyzer.importance')}
                      <FilterListIcon fontSize="small" sx={{ ml: 0.5, color: customState.importanceFilter ? 'primary.main' : 'action.disabled' }} />
                    </Box>
                  </TableCell>
                  <TableCell sx={jobTableColumnSx.matching}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={(e) => {
                      // Handle matching filter menu click
                        const anchorEl = e.currentTarget;
                        // Set anchor for filter menu
                        updateCustomState(prevState => ({
                          ...prevState,
                          matchingFilterAnchorEl: anchorEl
                        }));
                      }}>
                      {t('jobAnalyzer.matching')}
                      <FilterListIcon fontSize="small" sx={{ ml: 0.5, color: customState.matchingFilter ? 'primary.main' : 'action.disabled' }} />
                    </Box>
                  </TableCell>
                  {!viewOnly && <TableCell className="table-actions-cell" sx={jobTableColumnSx.actions}>{t('common.actions')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {skills.map((skill, index) => {
                // Create a unique key that includes matching data to force re-render when regenerated
                  const uniqueKey = `${index}-${skill.name || skill.skill}-${skill.matching?.percentage || 'no-match'}-${analysisResult?.regenerateTimestamp || ''}`;
                  return (
                    <TableRow key={uniqueKey}>
                      <TableCell sx={jobTableColumnSx.skill}>
                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                          {skill.name || skill.skill}
                        </Typography>
                      </TableCell>
                      <TableCell sx={jobTableColumnSx.category}>{skill.category}</TableCell>
                      <TableCell sx={jobTableColumnSx.importance}>
                        <Chip
                          label={getImportanceLabel(skill.importance || skill.level)}
                          color={getImportanceColor(skill.importance || skill.level)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={jobTableColumnSx.matching}>
                        {renderMatchingProgress(skill)}
                      </TableCell>
                      {!viewOnly && (
                        <TableCell className="table-actions-cell" sx={jobTableColumnSx.actions}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (startEditing) {
                                startEditing(skill, index);
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveSkill(index, analysisResult, setAnalysisResult)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Category Filter Menu */}
        <Popover
          open={Boolean(customState.categoryFilterAnchorEl)}
          anchorEl={customState.categoryFilterAnchorEl}
          onClose={handleCategoryFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          hideBackdrop={true}
          disablePortal={true}
        >
          <Box sx={{ p: 1, minWidth: 150 }}>
            <MenuItem
              value=""
              onClick={() => handleCategoryFilterSelect('')}
              sx={{
                fontWeight: customState.categoryFilter === '' ? 'bold' : 'normal'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                {t('common.all')}
                {customState.categoryFilter === '' ?
                  <FilterAltIcon fontSize="small" color="primary" /> : null}
              </Box>
            </MenuItem>

            {categories.map((category) => (
              <MenuItem
                key={category}
                value={category}
                onClick={() => handleCategoryFilterSelect(category)}
                sx={{ fontWeight: customState.categoryFilter === category ? 'bold' : 'normal' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  {category}
                  {customState.categoryFilter === category ?
                    <FilterAltIcon fontSize="small" color="primary" /> : null}
                </Box>
              </MenuItem>
            ))}
          </Box>
        </Popover>

        {/* Importance Filter Menu */}
        <Popover
          open={Boolean(customState.importanceFilterAnchorEl)}
          anchorEl={customState.importanceFilterAnchorEl}
          onClose={handleImportanceFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          hideBackdrop={true}
          disablePortal={true}
        >
          <Box sx={{ p: 1, minWidth: 150 }}>
            <MenuItem
              value=""
              onClick={() => handleImportanceFilterSelect('')}
              sx={{
                fontWeight: customState.importanceFilter === '' ? 'bold' : 'normal'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                {t('common.all')}
                {customState.importanceFilter === '' ?
                  <FilterAltIcon fontSize="small" color="primary" /> : null}
              </Box>
            </MenuItem>

            {importanceLevels.map((level) => (
              <MenuItem
                key={level}
                value={level}
                onClick={() => handleImportanceFilterSelect(level)}
                sx={{ fontWeight: customState.importanceFilter === level ? 'bold' : 'normal' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  {getImportanceLabel(level)}
                  {customState.importanceFilter === level ?
                    <FilterAltIcon fontSize="small" color="primary" /> : null}
                </Box>
              </MenuItem>
            ))}
          </Box>
        </Popover>

        {/* Matching Filter Menu */}
        <Popover
          open={Boolean(customState.matchingFilterAnchorEl)}
          anchorEl={customState.matchingFilterAnchorEl}
          onClose={handleMatchingFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          hideBackdrop={true}
          disablePortal={true}
        >
          <Box sx={{ p: 1, minWidth: 150 }}>
            <MenuItem
              value=""
              onClick={() => handleMatchingFilterSelect('')}
              sx={{
                fontWeight: customState.matchingFilter === '' ? 'bold' : 'normal'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                {t('common.all')}
                {customState.matchingFilter === '' ?
                  <FilterAltIcon fontSize="small" color="primary" /> : null}
              </Box>
            </MenuItem>

            {['High', 'Medium', 'Low', 'N/A'].map((range) => (
              <MenuItem
                key={range}
                value={range}
                onClick={() => handleMatchingFilterSelect(range)}
                sx={{ fontWeight: customState.matchingFilter === range ? 'bold' : 'normal' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  {range} {range !== 'N/A' && (
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      {range === 'High' ? '(80%+)' : range === 'Medium' ? '(60-79%)' : '(<60%)'}
                    </Typography>
                  )}
                  {customState.matchingFilter === range ?
                    <FilterAltIcon fontSize="small" color="primary" /> : null}
                </Box>
              </MenuItem>
            ))}
          </Box>
        </Popover>
      </Box>
    );
  };

  // Function to check if company research exists
  const checkCompanyResearch = useCallback(async () => {
    const targetUserId = userId || currentUser?.id;
    if (!targetUserId) {
      setCheckingCompanyResearch(false);
      setCompanyResearchError(t('errors.userNotFound'));
      return;
    }

    setCheckingCompanyResearch(true);
    setCompanyResearchError(''); // Clear any previous errors
    fileLogger.info('JOB_ANALYZER', 'Checking if company research exists', { userId: targetUserId });

    try {
      const companyData = await jobAnalyzerService.loadCompanyData(targetUserId);
      const hasResearch = !!(companyData && companyData.report && companyData.report.trim() !== '');

      fileLogger.info('JOB_ANALYZER', `Company research check complete: ${hasResearch ? 'exists' : 'not found'}`, {
        userId: targetUserId,
        hasResearch
      });

      setCompanyResearchExists(hasResearch);
    } catch (error) {
      fileLogger.error('JOB_ANALYZER', 'Error checking company research', {
        userId: targetUserId,
        error: error.message
      });
      setCompanyResearchExists(false);
      setCompanyResearchError(error.message);
    } finally {
      setCheckingCompanyResearch(false);
    }
  }, [userId, currentUser?.id]);

  // Check for company research when component mounts or userId changes
  useEffect(() => {
    checkCompanyResearch();
  }, [checkCompanyResearch]);

  // Render loading state while checking for company research
  if (checkingCompanyResearch) {
    return (
      <LoadingState
        message={t('jobAnalyzer.checkingCompanyResearch')}
        component="JobAnalyzer-CompanyCheck"
      />
    );
  }

  // If company research doesn't exist, show message and prevent access
  if (!companyResearchExists) {
    return (
      <Box sx={{ p: 4 }}>
        <ErrorState
          error={companyResearchError || 'Company research required'}
          title={t('errors.companyResearchRequired')}
          message={t('jobAnalyzer.mustCompleteCompanyResearch')}
          severity="warning"
          component="JobAnalyzer-CompanyResearchMissing"
          showRetry={false}
        />
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.href = '/company-research'}
          >
            {t('jobAnalyzer.goToCompanyResearch')}
          </Button>
        </Box>
      </Box>
    );
  }

  // The main component using shared AnalyzerPage - only shown if company research exists
  return (
    <AnalyzerPage
      type="job"
      title={t('jobAnalyzer.title')}
      steps={[
        t('jobAnalyzer.uploadStep'),
        t('jobAnalyzer.reviewStep')
      ]}
      userId={userId}
      viewOnly={viewOnly}
      onComplete={onComplete}
      onStatusChange={onStatusChange}
      renderDataDisplay={renderDataDisplay}
      renderEditDialog={renderEditDialog}
      getInitialState={getInitialState}
      processFiles={processFiles}
      handleSaveEdit={(index, updatedItem, analysisResult, setAnalysisResult) => {
        logger.info('JOB_ANALYZER', 'Handling save edit', { index, updatedItem });
        if (!analysisResult || !analysisResult.skills) {
          throw new Error('Cannot save edit: analysis result or skills are missing');
        }

        const updatedSkills = [...analysisResult.skills];
        let operation = 'edit_skill';

        if (index === -1 || index >= updatedSkills.length) {
          // Adding new skill
          updatedSkills.push(updatedItem);
          operation = 'add_skill';
        } else {
          // Editing existing skill
          updatedSkills[index] = updatedItem;
          operation = 'edit_skill';
        }

        const updatedResult = { ...analysisResult, skills: updatedSkills };
        setAnalysisResult(updatedResult);

        logger.info('JOB_ANALYZER', 'Skills updated successfully', {
          skillsCount: updatedSkills.length,
          updatedSkill: updatedItem,
          operation
        });
        return updatedResult;
      }}
    />
  );
}

export default JobAnalyzerPage;
